import {
  selectDeviceTypeMap,
  selectSystemTypeMap,
} from 'features/Integrations/state/selectors/integrationSelectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  buildConnectedSystemsHierarchy,
  buildDevicesBySystemAndDeviceType,
} from './systemStructure.hierarchy';
import { createSystemStructure, DEVICE_TYPE_ENDPOINT_EXPECTATION } from './systemStructure.mapper';
import {
  getDeviceTypeMeta,
  getDeviceTypesForSystem,
  getLegacyDeviceTypeMap,
  getSystemMeta,
} from './systemStructure.selectors';

export default function useSystemStructure() {
  // Reads available system + device types from integration state.
  const systemTypeMap = useSelector(selectSystemTypeMap);
  const deviceTypeMap = useSelector(selectDeviceTypeMap);

  const structure = useMemo(
    () =>
      createSystemStructure({
        systemTypeMap,
        deviceTypeMap,
        deviceTypeEndpointExpectation: DEVICE_TYPE_ENDPOINT_EXPECTATION,
      }), // Normalizes raw maps into categories, types, and lookup maps.
    [systemTypeMap, deviceTypeMap], // Rebuilds only when backend/store metadata changes.
  );

  return useMemo(
    () => ({
      structure, // Full normalized structure for advanced consumers.
      systemTypes: structure.systemTypes, // Flat list of systems with their device types.
      deviceTypes: structure.deviceTypes, // Flat list of normalized device type metadata.
      categories: structure.categories, // Grouped structure used for category/type displays.
      getSystemMeta: (systemType) => getSystemMeta(structure, systemType), // Looks up one system by name.
      getDeviceTypeMeta: (systemType, deviceType) =>
        getDeviceTypeMeta(structure, systemType, deviceType), // Looks up one device type under one system.
      getDeviceTypesForSystem: (systemType) => getDeviceTypesForSystem(structure, systemType), // Gets all device types for a system.
      getLegacyDeviceTypeMap: () => getLegacyDeviceTypeMap(structure), // Recreates the older map shape for legacy consumers.
      buildDevicesBySystemAndDeviceType, // Groups connected devices by system and device type.
      buildConnectedSystemsHierarchy: (args) =>
        buildConnectedSystemsHierarchy({ ...args, structure }), // Builds the Connected Systems UI tree using this structure.
    }),
    [structure], // Keeps helper identities stable until the structure changes.
  );
}
